import { useState, useRef, useCallback, useEffect } from 'react';
import { aiApi } from '../api';
import { AIMessage, ClickableSuggestion, PendingConfirmation, FileAttachment } from '../components/types';
import { ChatInputRef } from '../components/ChatInput';
import { extractAssistantText } from './useConversations';
import { getDraftMessage, setDraftMessage } from '../utils/localStorage';

/**
 * Generate contextual next-step suggestions based on the task that was just executed.
 */
function getNextSteps(taskName: string, result: any): string | null {
  const name = (taskName || '').toLowerCase();

  // Framework / template creation
  if (name.includes('framework') || name.includes('template') || name.includes('standard')) {
    const frameworkName = result?.name || result?.title || 'the framework';
    return [
      `• "Show me the structure of ${frameworkName}"`,
      `• "Add more requirements to ${frameworkName}"`,
      `• "Assign ${frameworkName} to a company"`,
      `• "List all frameworks"`,
    ].join('\n');
  }

  // User creation
  if (name.includes('create_user') || name.includes('create_company_admin')) {
    return [
      '• "Show me all users"',
      '• "Assign a role to this user"',
      '• "Create another user"',
    ].join('\n');
  }

  // User status changes
  if (name.includes('deactivate') || name.includes('reactivate') || name.includes('suspend')) {
    return [
      '• "Show me all users"',
      '• "Check this user\'s activity log"',
    ].join('\n');
  }

  // Company creation
  if (name.includes('company') || name.includes('organisation')) {
    return [
      '• "Show me all companies"',
      '• "Create an admin for this company"',
      '• "Assign a framework to this company"',
    ].join('\n');
  }

  // Messaging / send message
  if (name.includes('send_message') || name.includes('send_notification') || name.includes('send_email')) {
    return [
      '• "Send another message"',
      '• "Show my sent messages"',
      '• "Check inbox for replies"',
    ].join('\n');
  }

  // GDPR / compliance
  if (name.includes('gdpr') || name.includes('compliance') || name.includes('evidence')) {
    return [
      '• "Show compliance status"',
      '• "List pending evidence"',
      '• "Generate a compliance report"',
    ].join('\n');
  }

  // Generic fallback
  return '• Ask me to show what was created or take the next step';
}

interface UseChatParams {
  conversationId: string | null;
  currentCompanyId: number | null | undefined;
  ensureConversationId: () => Promise<string | null>;
  setConversations: React.Dispatch<React.SetStateAction<any[]>>;
  generateSmartTitle: (userMessage: string, aiResponse: string) => string;
  // Shared state from provider
  messages: AIMessage[];
  setMessages: React.Dispatch<React.SetStateAction<AIMessage[]>>;
  setSuggestions: React.Dispatch<React.SetStateAction<ClickableSuggestion[]>>;
}

interface UseChatReturn {
  inputValue: string;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  isLoading: boolean;
  isStreaming: boolean;
  statusMessage: string;
  pendingConfirmation: PendingConfirmation | null;
  setPendingConfirmation: React.Dispatch<React.SetStateAction<PendingConfirmation | null>>;
  attachments: FileAttachment[];
  setAttachments: React.Dispatch<React.SetStateAction<FileAttachment[]>>;
  chatInputRef: React.RefObject<ChatInputRef>;
  handleSendMessage: (overrideMessage?: string) => Promise<void>;
  handleConfirmAction: () => Promise<void>;
  handleCancelConfirmation: () => void;
  handleSuggestionClick: (suggestion: ClickableSuggestion) => void;
  handleEditMessage: (messageIndex: number) => void;
  handleStopProcessing: () => void;
}

export function useChat({
  conversationId,
  currentCompanyId,
  ensureConversationId,
  setConversations,
  generateSmartTitle,
  messages,
  setMessages,
  setSuggestions,
}: UseChatParams): UseChatReturn {
  const [inputValue, setInputValue] = useState(() => getDraftMessage());
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null);
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);

  // Persist draft input
  useEffect(() => { setDraftMessage(inputValue); }, [inputValue]);

  const chatInputRef = useRef<ChatInputRef>(null!);
  const abortControllerRef = useRef<AbortController | null>(null);
  const streamingMessageIdRef = useRef<string | null>(null);

  const handleStopProcessing = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  const handleEditMessage = useCallback((messageIndex: number) => {
    const message = messages[messageIndex];
    if (!message || message.role !== 'user') return;

    setInputValue(message.content);
    setMessages(prev => prev.slice(0, messageIndex));
    setPendingConfirmation(null);

    setTimeout(() => chatInputRef.current?.focus(), 50);
  }, [messages, setMessages]);

  const handleSendMessage = useCallback(async (overrideMessage?: string) => {
    const resolvedMessage = (overrideMessage ?? inputValue).trim();
    if (!resolvedMessage || isLoading || isStreaming) return;

    const activeConversationId = await ensureConversationId();
    if (!activeConversationId) {
      console.error('Failed to get conversation ID');
      return;
    }

    const now = new Date();
    const userMessage: AIMessage = {
      id: `user_${now.getTime()}`,
      role: 'user',
      content: resolvedMessage,
      timestamp: now,
    };

    const isFirstUserMessage = !messages.some(m => m.role === 'user');
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setAttachments([]);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    streamingMessageIdRef.current = null;

    setIsStreaming(true);
    setStatusMessage('Analyzing your request...');

    try {
      const response = await aiApi.processAIRequestStreaming(
        resolvedMessage,
        activeConversationId,
        (chunk: string) => {
          const safeChunk = chunk || '';
          setStatusMessage('Composing response...');
          setMessages(prev => {
            const existingId = streamingMessageIdRef.current;
            if (!existingId) {
              const newId = `streaming_${Date.now()}`;
              streamingMessageIdRef.current = newId;
              return [...prev, {
                id: newId,
                role: 'assistant',
                content: '',
                timestamp: new Date(),
                isStreaming: true,
                streamingContent: safeChunk,
              } as AIMessage];
            }
            return prev.map(m =>
              m.id === existingId
                ? { ...m, streamingContent: (m.streamingContent || '') + safeChunk } as AIMessage
                : m
            );
          });
        },
        abortController.signal
      );

      abortControllerRef.current = null;
      setStatusMessage('');

      const finalContent = extractAssistantText(response.message);
      const streamingId = streamingMessageIdRef.current;
      streamingMessageIdRef.current = null;

      const meta = {
        companyId: currentCompanyId as number | null,
        ...(response.audit_log_id && { auditLogId: response.audit_log_id as string }),
        ...(response.execution_time_ms && { executionTimeMs: response.execution_time_ms as number })
      };

      const withoutStreaming = (prev: AIMessage[]) =>
        prev.filter(m => !m.id.startsWith('streaming_'));

      if (response.response_type === 'confirmation_required') {
        const confirmMsg: AIMessage = {
          id: `confirm_${Date.now()}`,
          role: 'assistant',
          content: finalContent || 'I need your confirmation to proceed with this action.',
          timestamp: new Date(),
          meta
        };
        setMessages(prev => [...withoutStreaming(prev), confirmMsg]);

        if (response.confirmation_data) {
          setPendingConfirmation({
            confirmationId: response.confirmation_data.confirmation_id,
            confirmationHash: response.confirmation_data.confirmation_hash || '',
            message: response.message || 'Action requires confirmation',
            expiresAt: response.confirmation_data.expires_at,
            riskLevel: response.confirmation_data.risk_level || 'medium',
            taskName: response.confirmation_data.task_name || 'AI Operation',
            options: []
          });
        }
      } else if (response.response_type === 'data') {
        const dataMessage: AIMessage = {
          id: `data_${now.getTime()}`,
          role: 'assistant',
          content: response.message,
          timestamp: now,
          meta: {
            dataResponse: true,
            rawData: response.execution_result,
            companyId: currentCompanyId as number | null,
            ...(response.audit_log_id && { auditLogId: response.audit_log_id }),
            ...(response.execution_time_ms && { executionTimeMs: response.execution_time_ms })
          }
        };
        setMessages(prev => [...withoutStreaming(prev), dataMessage]);
        setTimeout(() => chatInputRef.current?.focus(), 100);
      } else {
        setMessages(prev => {
          if (!streamingId) {
            // Prevent duplicate — check if last message has same content
            const last = prev[prev.length - 1];
            if (last?.role === 'assistant' && last?.content === finalContent) return prev;
            return [...prev, {
              id: `ai_${Date.now()}`,
              role: 'assistant',
              content: finalContent,
              timestamp: new Date(),
              isStreaming: false,
              meta
            } as AIMessage];
          }
          return prev.map(m =>
            m.id === streamingId
              ? { ...m, content: finalContent, isStreaming: false, streamingContent: undefined, meta } as AIMessage
              : m
          );
        });
      }

      if (isFirstUserMessage) {
        const smartTitle = generateSmartTitle(resolvedMessage, finalContent);
        aiApi.updateConversationTitle(activeConversationId, smartTitle)
          .then(() => {
            setConversations((prev: any[]) => prev.map((c: any) =>
              c.id === activeConversationId ? { ...c, title: smartTitle } : c
            ));
          })
          .catch(err => console.warn('Failed to auto-update title:', err));
      }

    } catch (error: any) {
      abortControllerRef.current = null;
      streamingMessageIdRef.current = null;

      if (error?.name === 'AbortError') {
        setMessages(prev => {
          const filtered = prev.filter(m => !m.id.startsWith('streaming_'));
          return [...filtered, {
            id: `stopped_${Date.now()}`,
            role: 'assistant',
            content: '⏹ Response stopped.',
            timestamp: new Date(),
          } as AIMessage];
        });
      } else {
        console.error('Failed to send message:', error);
        setMessages(prev => {
          const filtered = prev.filter(m => !m.id.startsWith('streaming_'));
          return [...filtered, {
            id: `error_${Date.now()}`,
            role: 'assistant',
            content: 'Sorry, I encountered an error processing your request. Please try again.',
            timestamp: new Date(),
          } as AIMessage];
        });
      }
    } finally {
      setIsStreaming(false);
      setStatusMessage('');
      setTimeout(() => chatInputRef.current?.focus(), 100);
    }
  }, [inputValue, isLoading, isStreaming, messages, ensureConversationId, currentCompanyId, generateSmartTitle, setConversations, setMessages]);

  const handleConfirmAction = useCallback(async () => {
    if (!pendingConfirmation) return;

    const activeConversationId = conversationId;
    if (!activeConversationId) {
      console.error('No active conversation for confirmation execution');
      return;
    }

    setIsLoading(true);

    try {
      const response = await aiApi.executeConfirmedTask(
        pendingConfirmation.confirmationId,
        pendingConfirmation.confirmationHash
      );

      setPendingConfirmation(null);

      const now = new Date();
      let resultContent = '';
      let isSuccess = false;

      if (response.response_type === 'executed') {
        isSuccess = true;
        const result = response.execution_result;
        const taskName = pendingConfirmation.taskName;

        if ((taskName === 'create_user' || taskName === 'create_user_account' || taskName === 'create_company_admin') && result) {
          const isAdmin = taskName === 'create_company_admin';
          resultContent = isAdmin
            ? '✅ **Company admin account created successfully!**\n\n'
            : '✅ **User account created successfully!**\n\n';
          resultContent += '**Account Details:**\n';

          if (result.first_name && result.last_name) {
            resultContent += `• Name: ${result.first_name} ${result.last_name}\n`;
          }
          if (result.email) resultContent += `• Email: ${result.email}\n`;
          if (result.company_id) resultContent += `• Company: ID ${result.company_id}\n`;
          if (result.user_id || result.id) resultContent += `• User ID: ${result.user_id || result.id}\n`;
          if (result.status) resultContent += `• Status: ${result.status}\n`;
          resultContent += '\n✓ User can log in immediately with their credentials.';

        } else if (taskName === 'reactivate_user' && result) {
          resultContent = result.reactivated
            ? `✅ **User account reactivated successfully!**\n\n• User ID: ${result.user_id}\n✓ The user can now log in again.`
            : `ℹ️ User (ID: ${result.user_id}) is already active — no changes made.`;
        } else if (taskName === 'deactivate_user' && result) {
          resultContent = result.deactivated
            ? `✅ **User account deactivated.**\n\n• User ID: ${result.user_id}\n• Reason: ${result.reason || 'N/A'}`
            : `ℹ️ User (ID: ${result.user_id}) was already inactive — no changes made.`;

        } else if ((taskName === 'send_message' || taskName === 'send_notification' || taskName === 'send_email') && result) {
          // Build recipient display name
          const recipientName = (result.recipient_first_name && result.recipient_last_name)
            ? `${result.recipient_first_name} ${result.recipient_last_name}`
            : result.recipient_name || result.recipient_email || 'the recipient';
          const recipientEmail = result.recipient_email ? ` (${result.recipient_email})` : '';
          const subject = result.subject || 'your message';

          resultContent = `✅ **Message sent successfully!**\n\n`;
          resultContent += `Your message has been delivered to **${recipientName}**${recipientEmail}.\n\n`;
          resultContent += `**Message summary:**\n`;
          resultContent += `• Subject: ${subject}\n`;
          if (result.message_type) resultContent += `• Type: ${result.message_type.replace(/_/g, ' ')}\n`;
          if (result.priority && result.priority !== 'normal') resultContent += `• Priority: ${result.priority}\n`;
          if (result.id) resultContent += `• Message ID: ${result.id}\n`;
          resultContent += `\nIf ${recipientName.split(' ')[0]} is offline, they'll receive an **email notification** with a direct link to view and reply.`;

        } else {
          // Generic task result — show full server message + all result details
          resultContent = '✅ ' + (response.message || 'Action completed successfully.');

          if (result && typeof result === 'object') {
            const details: string[] = [];

            // Common identifiers
            if (result.id) details.push(`ID: ${result.id}`);
            if (result.name) details.push(`Name: ${result.name}`);
            if (result.title) details.push(`Title: ${result.title}`);
            if (result.first_name && result.last_name) details.push(`Name: ${result.first_name} ${result.last_name}`);
            if (result.email) details.push(`Email: ${result.email}`);
            if (result.status) details.push(`Status: ${result.status}`);
            if (result.company_id) details.push(`Company ID: ${result.company_id}`);

            // Framework / template specifics
            if (result.framework_id) details.push(`Framework ID: ${result.framework_id}`);
            if (result.template_id) details.push(`Template ID: ${result.template_id}`);
            if (result.version) details.push(`Version: ${result.version}`);
            if (result.scopes_count || result.scopes) details.push(`Scopes: ${result.scopes_count ?? (Array.isArray(result.scopes) ? result.scopes.length : result.scopes)}`);
            if (result.sections_count || result.sections) details.push(`Sections: ${result.sections_count ?? (Array.isArray(result.sections) ? result.sections.length : result.sections)}`);
            if (result.requirements_count || result.requirements) details.push(`Requirements: ${result.requirements_count ?? (Array.isArray(result.requirements) ? result.requirements.length : result.requirements)}`);
            if (result.controls_count || result.controls) details.push(`Controls: ${result.controls_count ?? (Array.isArray(result.controls) ? result.controls.length : result.controls)}`);

            // Counts / stats
            if (result.total) details.push(`Total items: ${result.total}`);
            if (result.created) details.push(`Created: ${result.created}`);
            if (result.updated) details.push(`Updated: ${result.updated}`);

            // Catch remaining string/number fields not already listed
            const shown = new Set(['id','name','title','first_name','last_name','email','status','company_id','framework_id','template_id','version','scopes_count','scopes','sections_count','sections','requirements_count','requirements','controls_count','controls','total','created','updated']);
            for (const [key, val] of Object.entries(result)) {
              if (shown.has(key)) continue;
              if (val === null || val === undefined) continue;
              if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
                const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                details.push(`${label}: ${val}`);
              }
            }

            if (details.length > 0) {
              resultContent += '\n\n**Details:**\n' + details.map(d => `• ${d}`).join('\n');
            }
          }
        }

        // Audit trail
        if (response.audit_log_id || response.execution_time_ms) {
          resultContent += '\n\n**Audit Trail:**\n';
          if (response.audit_log_id) resultContent += `• Log ID: \`${response.audit_log_id}\`\n`;
          if (response.execution_time_ms) resultContent += `• Execution Time: ${response.execution_time_ms}ms\n`;
        }

        // Suggested next steps based on task type
        const nextSteps = getNextSteps(taskName, result);
        if (nextSteps) {
          resultContent += '\n\n**What you can do next:**\n' + nextSteps;
        }

      } else if (response.response_type === 'error') {
        resultContent = '❌ ' + (response.message || 'Execution failed. Please try again.');
      } else if (response.response_type === 'denied') {
        resultContent = '⛔ ' + (response.message || 'Action was denied.');
      }

      const resultMessage: AIMessage = {
        id: `result_${now.getTime()}`,
        role: 'assistant',
        content: resultContent,
        timestamp: now,
        meta: {
          companyId: currentCompanyId,
          ...(response.audit_log_id && { auditLogId: response.audit_log_id }),
          ...(response.execution_time_ms && { executionTimeMs: response.execution_time_ms }),
          confirmationExecuted: true
        }
      };

      setMessages(prev => [...prev, resultMessage]);
      setTimeout(() => chatInputRef.current?.focus(), 100);

      try {
        await aiApi.appendConversationMessage(
          activeConversationId,
          'assistant',
          resultContent,
          {
            companyId: currentCompanyId,
            ...(response.audit_log_id && { auditLogId: response.audit_log_id }),
            ...(response.execution_time_ms && { executionTimeMs: response.execution_time_ms }),
            confirmationExecuted: true
          }
        );
      } catch (persistError) {
        console.error('Failed to persist confirmation result:', persistError);
      }

      if (isSuccess) {
        console.log('✅ Confirmation executed successfully:', response);
      }

    } catch (error) {
      console.error('Failed to execute confirmed action:', error);

      const errorMessage: AIMessage = {
        id: `error_${Date.now()}`,
        role: 'assistant',
        content: '❌ Failed to execute the confirmed action. Please try again or contact support.',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [pendingConfirmation, conversationId, currentCompanyId, setMessages]);

  const handleCancelConfirmation = useCallback(() => {
    setPendingConfirmation(null);
  }, []);

  const handleSuggestionClick = useCallback((suggestion: ClickableSuggestion) => {
    if (suggestion.action === 'send_message') {
      handleSendMessage(suggestion.value);
    }
  }, [handleSendMessage]);

  return {
    inputValue,
    setInputValue,
    isLoading,
    isStreaming,
    statusMessage,
    pendingConfirmation,
    setPendingConfirmation,
    attachments,
    setAttachments,
    chatInputRef,
    handleSendMessage,
    handleConfirmAction,
    handleCancelConfirmation,
    handleSuggestionClick,
    handleEditMessage,
    handleStopProcessing,
  };
}
